// pages/CreditDistributionHistory.tsx — immutable credit distribution log
import { useState, useEffect, useCallback } from 'react';
import { Calendar, Search, TrendingUp, Users, DollarSign, Filter } from 'lucide-react';
import useAdmin from '../hooks/useAdmin';

interface DistributionRecord {
  id: string;
  admin_login_id?: string;
  admin_name?: string;
  recipient_type: 'manager' | 'user';
  recipient_login_id?: string;
  recipient_name?: string;
  amount: number;
  notes?: string;
  distributed_at: string;
}

interface AdminSummary {
  admin_login_id?: string;
  admin_name?: string;
  tx_count: number;
  total_distributed?: number; // admin_summary uses this
  total?: number;             // FIX: today_summary uses this field
}

interface HistoryData {
  history: DistributionRecord[];
  total_records: number;
  overall_total_distributed: number;
  admin_summary: AdminSummary[];
  today_summary: AdminSummary[];
}

export default function CreditDistributionHistory() {
  const { token, refreshCsrf } = useAdmin();
  const [data, setData] = useState<HistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterAdmin, setFilterAdmin] = useState('');
  const [activeTab, setActiveTab] = useState<'overall' | 'admin' | 'today' | 'history'>('overall');

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const csrfToken = await refreshCsrf();
      const params = new URLSearchParams({ limit: '500' });
      if (filterDate) {
        params.set('date_from', filterDate + 'T00:00:00');
        params.set('date_to', filterDate + 'T23:59:59');
      }
      if (filterAdmin) params.set('admin_login_id', filterAdmin);

      const res = await fetch(`/admin/credit-distribution-history?${params}`, {
        headers: { Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrfToken || '' },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch history');
      setData(await res.json());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, refreshCsrf, filterDate, filterAdmin]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const fmt = (n: number) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const fmtDate = (s: string) => new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

  // FIX: today_summary returns 'total' field, admin_summary returns 'total_distributed'
  const todayTotal = data?.today_summary.reduce((s, r) => s + Number(r.total ?? 0), 0) ?? 0;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Credit Distribution History</h2>
          <p className="text-sm text-gray-500 mt-1">Immutable audit log — all records are permanent and cannot be modified or deleted</p>
        </div>
        <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-300 px-3 py-2 rounded-lg">
          <span className="text-yellow-600 text-sm font-medium">🔒 Read-Only Log</span>
        </div>
      </div>

      {/* Overall Stats */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-5 rounded-xl shadow">
            <div className="flex items-center gap-3">
              <TrendingUp size={28} />
              <div>
                <p className="text-indigo-100 text-sm">Total Credits Distributed (All Time)</p>
                <p className="text-3xl font-bold">${fmt(data.overall_total_distributed)}</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-5 rounded-xl shadow">
            <div className="flex items-center gap-3">
              <DollarSign size={28} />
              <div>
                <p className="text-green-100 text-sm">Today's Distribution</p>
                {/* FIX: use pre-computed todayTotal instead of inline r.total */}
                <p className="text-3xl font-bold">${fmt(todayTotal)}</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white p-5 rounded-xl shadow">
            <div className="flex items-center gap-3">
              <Users size={28} />
              <div>
                <p className="text-blue-100 text-sm">Total Transactions</p>
                <p className="text-3xl font-bold">{data.total_records}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        {(['overall', 'admin', 'today', 'history'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === tab ? 'bg-white shadow text-indigo-600' : 'text-gray-600 hover:text-gray-800'}`}>
            {tab === 'overall' ? '📊 Overall' : tab === 'admin' ? '👤 Admin-wise' : tab === 'today' ? '📅 Today' : '📋 All Records'}
          </button>
        ))}
      </div>

      {/* Filters */}
      {activeTab === 'history' && (
        <div className="flex flex-wrap gap-3 mb-4 p-4 bg-gray-50 rounded-xl border">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-500" />
            <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="flex items-center gap-2">
            <Search size={16} className="text-gray-500" />
            <input type="text" placeholder="Filter by Admin Login ID" value={filterAdmin}
              onChange={e => setFilterAdmin(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 w-48" />
          </div>
          <button onClick={() => { setFilterDate(''); setFilterAdmin(''); }}
            className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm flex items-center gap-1">
            <Filter size={14} /> Clear
          </button>
        </div>
      )}

      {loading && <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>}
      {error && <div className="p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>}

      {/* Admin-wise Summary */}
      {activeTab === 'admin' && data && (
        <div className="bg-white rounded-xl shadow border overflow-hidden">
          <table className="w-full">
            <thead className="bg-indigo-50">
              <tr>{['Admin Login ID', 'Admin Name', 'Transactions', 'Total Distributed'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.admin_summary.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono font-bold text-indigo-600">{row.admin_login_id || '—'}</td>
                  <td className="px-4 py-3 text-gray-800">{row.admin_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{row.tx_count}</td>
                  <td className="px-4 py-3 font-bold text-green-700">${fmt(Number(row.total_distributed ?? 0))}</td>
                </tr>
              ))}
              {data.admin_summary.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No data</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Today's Summary */}
      {activeTab === 'today' && data && (
        <div className="bg-white rounded-xl shadow border overflow-hidden">
          <div className="px-4 py-3 bg-blue-50 border-b">
            <p className="text-sm font-medium text-blue-700">Today: {new Date().toLocaleDateString('en-IN', { dateStyle: 'full' })}</p>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>{['Admin Login ID', 'Admin Name', 'Transactions', 'Total Today'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.today_summary.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono font-bold text-indigo-600">{row.admin_login_id || '—'}</td>
                  <td className="px-4 py-3 text-gray-800">{row.admin_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{row.tx_count}</td>
                  {/* FIX: row.total is the field for today_summary */}
                  <td className="px-4 py-3 font-bold text-green-700">${fmt(Number(row.total ?? 0))}</td>
                </tr>
              ))}
              {data.today_summary.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No distributions today</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Full History */}
      {(activeTab === 'history' || activeTab === 'overall') && data && (
        <div className="bg-white rounded-xl shadow border overflow-hidden">
          {activeTab === 'overall' && (
            <div className="px-4 py-3 bg-gray-50 border-b">
              <p className="text-sm text-gray-600 font-medium">Most Recent 20 Transactions</p>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>{['Date & Time', 'Admin (Who)', 'Recipient (To)', 'Type', 'Amount'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(activeTab === 'overall' ? data.history.slice(0, 20) : data.history).map(row => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{fmtDate(row.distributed_at)}</td>
                    <td className="px-4 py-3">
                      <p className="font-mono text-xs text-indigo-600 font-bold">{row.admin_login_id || 'SYSTEM'}</p>
                      <p className="text-sm text-gray-700">{row.admin_name || '—'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-mono text-xs text-purple-600 font-bold">{row.recipient_login_id || '—'}</p>
                      <p className="text-sm text-gray-700">{row.recipient_name || '—'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.recipient_type === 'manager' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                        {row.recipient_type === 'manager' ? '👔 Manager' : '🎮 User'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold text-green-700 text-lg">${fmt(row.amount)}</td>
                  </tr>
                ))}
                {data.history.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No distribution records found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
